import React, { useEffect, useState, useCallback } from 'react';
import AppLayout from '../../components/layout/AppLayout';
import StatusBadge from '../../components/ui/StatusBadge';
import Modal from '../../components/ui/Modal';
import FormInput from '../../components/forms/FormInput';
import FormSelect from '../../components/forms/FormSelect';
import FormTextarea from '../../components/forms/FormTextarea';
import { getQuotations, createQuotation, deleteQuotation, selectQuotation, rejectQuotation, compareQuotations, parseQuotationDocument } from '../../api/quotations';
import { getCategories } from '../../api/serviceCategories';

const serviceTypes = [
  'Swimming Pool', 'Gym', 'Water Treatment Plant (WTP)', 'Sewage Treatment Plant (STP)',
  'Gas Refill', 'AVM Smart Kitchen, Gas Line', 'Kone Lift', 'CCC Zero Waste', 'Pollution',
  'Generator DG', 'DG Battery', 'Transformer', 'CCTV/Biometrics and Intercom',
  'Solar WattSun Energy System', 'Hybrid Hot Water Heater', 'Pumps in Water Tanks',
  'EV Charger', 'Invertor', 'Fire',
];

const tabs = [
  { key: 'pending', label: 'Pending Review' },
  { key: 'upload', label: 'Upload New' },
  { key: 'compare', label: 'Compare' },
  { key: 'selected', label: 'Selected' },
  { key: 'rejected', label: 'Archived' },
];

const serviceEmojis = {
  security: '🔐', cleaning: '🧹', 'water-treatment': '💧', 'water treatment': '💧',
  garden: '🌿', 'garden management': '🌿', 'swimming pool': '🏊', pool: '🏊',
  pest: '🐛', elevator: '🛗', electrical: '⚡', plumbing: '🔧', fire: '🧯',
  intercom: '📞', generator: '⚙️', garbage: '🗑️',
};

const getServiceEmoji = (name) => {
  if (!name) return '📄';
  const lower = name.toLowerCase();
  for (const [key, emoji] of Object.entries(serviceEmojis)) {
    if (lower.includes(key)) return emoji;
  }
  return '📄';
};

export default function QuotationsPage() {
  const [quotations, setQuotations] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [showModal, setShowModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [filterService, setFilterService] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [compareServiceId, setCompareServiceId] = useState('');
  const [compareData, setCompareData] = useState([]);
  const [compareLoaded, setCompareLoaded] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [documentFile, setDocumentFile] = useState(null);
  const [form, setForm] = useState({
    service_category_id: '', custom_category_name: '', vendor_name: '', quoted_amount: '',
    validity_date: '', description: '',
  });

  const fetchQuotations = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page };
      if (activeTab === 'pending') params.status = 'pending';
      else if (activeTab === 'selected') params.status = 'selected';
      else if (activeTab === 'rejected') params.status = 'rejected';
      if (filterService) params.service_category_id = filterService;
      const res = await getQuotations(params);
      setQuotations(res.data?.data || []);
      setMeta(res.data?.meta || res.data || { current_page: 1, last_page: 1 });
    } catch (err) {
      console.error('Failed to load quotations', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, filterService]);

  useEffect(() => {
    if (activeTab !== 'upload' && activeTab !== 'compare') {
      fetchQuotations();
    }
  }, [fetchQuotations, activeTab]);

  useEffect(() => {
    getCategories().then((res) => setCategories(res.data?.data || [])).catch(() => {});
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setDocumentFile(file);
    setParsing(true);
    try {
      const res = await parseQuotationDocument(file);
      const extracted = res.data || {};
      const parsedCat = categories.find((c) => c.id === extracted.service_category_id);
      const parsedName = parsedCat?.name || '';
      const isKnown = serviceTypes.includes(parsedName);
      setForm((prev) => ({
        ...prev,
        service_category_id: parsedName ? (isKnown ? parsedName : '__other__') : prev.service_category_id,
        custom_category_name: parsedName && !isKnown ? parsedName : prev.custom_category_name,
        vendor_name: extracted.vendor_name || prev.vendor_name,
        quoted_amount: extracted.quoted_amount || prev.quoted_amount,
        validity_date: extracted.validity_date || prev.validity_date,
        description: extracted.description || prev.description,
      }));
    } catch (err) {
      console.error('Failed to parse document', err);
    } finally {
      setParsing(false);
    }
  };

  const resetForm = () => {
    setForm({ service_category_id: '', custom_category_name: '', vendor_name: '', quoted_amount: '', validity_date: '', description: '' });
    setDocumentFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.service_category_id) return;
    if (form.service_category_id === '__other__' && !form.custom_category_name.trim()) return;
    setSubmitting(true);
    try {
      const submitData = { ...form };
      if (submitData.service_category_id === '__other__') {
        submitData.custom_category_name = submitData.custom_category_name.trim();
      } else {
        submitData.custom_category_name = submitData.service_category_id;
      }
      delete submitData.service_category_id;
      if (documentFile) {
        submitData.document = documentFile;
      }
      await createQuotation(submitData);
      setShowModal(false);
      resetForm();
      setActiveTab('pending');
      fetchQuotations();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to create quotation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSelect = async (id) => {
    if (!window.confirm('Are you sure you want to select this quotation? This will mark it as the chosen vendor.')) return;
    try {
      await selectQuotation(id);
      fetchQuotations();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to select quotation');
    }
  };

  const handleRejectOpen = (id) => {
    setRejectingId(id);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const handleRejectSubmit = async () => {
    try {
      await rejectQuotation(rejectingId, { rejection_reason: rejectionReason });
      setShowRejectModal(false);
      setRejectingId(null);
      setRejectionReason('');
      fetchQuotations();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to reject quotation');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this quotation?')) return;
    try {
      await deleteQuotation(id);
      fetchQuotations();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete quotation');
    }
  };

  const handleCompare = async () => {
    if (!compareServiceId) return;
    try {
      const res = await compareQuotations(compareServiceId);
      setCompareData(res.data?.data || []);
      setCompareLoaded(true);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to load comparison');
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (val) => `₹${Number(val || 0).toLocaleString('en-IN')}`;

  const categoryOptions = [
    ...serviceTypes.map((t) => ({ value: t, label: t })),
    { value: '__other__', label: 'Other (enter manually)' },
  ];

  // Compare stats
  const lowestQuote = compareData.length > 0 ? Math.min(...compareData.map((q) => Number(q.quoted_amount || 0))) : 0;
  const highestQuote = compareData.length > 0 ? Math.max(...compareData.map((q) => Number(q.quoted_amount || 0))) : 0;
  const priceVariance = highestQuote > 0 ? Math.round(((highestQuote - lowestQuote) / highestQuote) * 100) : 0;

  return (
    <AppLayout title="Quotations" breadcrumbs="Quotations">
      {/* Sub Navigation */}
      <div
        className="flex overflow-x-auto -mx-8 -mt-8 mb-0"
        style={{
          background: 'white',
          borderBottom: '1px solid #E5E5E0',
          padding: '0 40px',
          gap: '32px',
        }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => {
              setActiveTab(tab.key);
              if (tab.key === 'upload') {
                resetForm();
                setShowModal(true);
              }
            }}
            className="whitespace-nowrap bg-transparent border-0 cursor-pointer transition-all"
            style={{
              padding: '16px 4px',
              fontSize: '14px',
              fontWeight: 500,
              fontFamily: "'Lexend', sans-serif",
              color: activeTab === tab.key ? '#043959' : '#666666',
              borderBottom: `3px solid ${activeTab === tab.key ? '#043959' : 'transparent'}`,
            }}
            onMouseEnter={(e) => {
              if (activeTab !== tab.key) {
                e.currentTarget.style.color = '#043959';
                e.currentTarget.style.borderBottomColor = '#F4A460';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== tab.key) {
                e.currentTarget.style.color = '#666666';
                e.currentTarget.style.borderBottomColor = 'transparent';
              }
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: '32px 0' }}>

        {/* Header */}
        <div className="flex justify-between items-center" style={{ marginBottom: '20px' }}>
          <h3 className="text-lg font-semibold" style={{ color: '#1A1A1A' }}>Quotation Management</h3>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="inline-flex items-center gap-2 text-white text-sm font-medium rounded-lg transition-all hover:opacity-90 border-0 cursor-pointer"
            style={{ padding: '10px 20px', background: '#043959', fontFamily: "'Lexend', sans-serif" }}
          >
            <span>+</span> Upload Quotation
          </button>
        </div>

        {/* Info Banner */}
        <div
          className="flex items-start gap-3"
          style={{
            padding: '16px 20px',
            background: 'rgba(59,130,246,0.05)',
            border: '1px solid rgba(59,130,246,0.2)',
            borderRadius: '12px',
            marginBottom: '24px',
          }}
        >
          <div style={{ fontSize: '20px', lineHeight: 1 }}>💡</div>
          <div>
            <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A', marginBottom: '4px' }}>Quotation Comparison Policy</h4>
            <p style={{ fontSize: '13px', color: '#666666', lineHeight: 1.5 }}>
              As per association guidelines, <strong>minimum 3 quotations</strong> must be collected and compared before finalizing any vendor. Use the comparison tool to make informed decisions.
            </p>
          </div>
        </div>

        {/* ============ COMPARE TAB ============ */}
        {activeTab === 'compare' && (
          <>
            {/* Service Selection */}
            <div
              className="bg-white"
              style={{ padding: '24px', borderRadius: '16px', border: '1px solid #E5E5E0', marginBottom: '24px' }}
            >
              <h4 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px', color: '#1A1A1A' }}>Select Service to Compare Quotations</h4>
              <div className="flex flex-wrap items-end gap-3">
                <div style={{ flex: 1, maxWidth: '400px' }}>
                  <label className="text-sm font-medium" style={{ color: '#666666', display: 'block', marginBottom: '6px' }}>Service Category:</label>
                  <select
                    value={compareServiceId}
                    onChange={(e) => { setCompareServiceId(e.target.value); setCompareLoaded(false); }}
                    className="w-full cursor-pointer"
                    style={{ padding: '10px 12px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '14px', fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none' }}
                  >
                    <option value="">Select a service to compare quotations</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={handleCompare}
                  disabled={!compareServiceId}
                  className="text-sm font-medium rounded-lg transition-all cursor-pointer text-white border-0"
                  style={{
                    padding: '10px 20px',
                    background: compareServiceId ? '#043959' : '#E5E5E0',
                    color: compareServiceId ? 'white' : '#999999',
                    fontFamily: "'Lexend', sans-serif",
                  }}
                >
                  Load Quotations
                </button>
              </div>
            </div>

            {/* Comparison Results */}
            {compareLoaded && compareData.length > 0 && (
              <>
                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4" style={{ gap: '16px', marginBottom: '24px' }}>
                  {[
                    { icon: '📊', value: compareData.length, label: 'Quotations Received' },
                    { icon: '💰', value: formatCurrency(lowestQuote), label: 'Lowest Quote' },
                    { icon: '📈', value: formatCurrency(highestQuote), label: 'Highest Quote' },
                    { icon: '📉', value: `${priceVariance}%`, label: 'Price Variance' },
                  ].map((s) => (
                    <div key={s.label} className="bg-white flex items-center gap-3" style={{ padding: '20px', borderRadius: '12px', border: '1px solid #E5E5E0' }}>
                      <div style={{ fontSize: '24px' }}>{s.icon}</div>
                      <div>
                        <div className="text-xl font-bold" style={{ color: '#1A1A1A' }}>{s.value}</div>
                        <div className="text-xs font-medium" style={{ color: '#666666' }}>{s.label}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Quotation Comparison Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginBottom: '24px' }}>
                  {compareData.map((q, idx) => {
                    const isLowest = Number(q.quoted_amount) === lowestQuote;
                    return (
                      <div
                        key={q.id}
                        className="bg-white"
                        style={{
                          border: isLowest ? '2px solid #10B981' : '2px solid #E5E5E0',
                          borderRadius: '16px',
                          padding: '24px',
                          transition: 'all 0.3s',
                          position: 'relative',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.08)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                      >
                        {/* Rank Badge */}
                        {idx === 0 && (
                          <div className="flex items-center gap-2" style={{ marginBottom: '12px' }}>
                            <span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, background: 'rgba(16,185,129,0.1)', color: '#10B981' }}>
                              Recommended
                            </span>
                            <span>⭐</span>
                          </div>
                        )}
                        {isLowest && idx !== 0 && (
                          <div style={{ marginBottom: '12px' }}>
                            <span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, background: 'rgba(59,130,246,0.1)', color: '#3B82F6' }}>
                              Lowest Price
                            </span>
                          </div>
                        )}

                        {/* Header */}
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: '#1A1A1A' }}>
                            {q.vendor?.name || 'Unknown Vendor'}
                          </div>
                          <div style={{ fontSize: '28px', fontWeight: 700, color: '#043959', marginBottom: '4px' }}>
                            {formatCurrency(q.quoted_amount)}
                          </div>
                        </div>

                        {/* Details */}
                        <ul style={{ listStyle: 'none', padding: 0, margin: '16px 0' }}>
                          {[
                            { label: 'Service', value: q.service_category?.name || '-' },
                            { label: 'Validity', value: formatDate(q.validity_date) },
                            { label: 'Status', value: q.status },
                          ].map((detail) => (
                            <li
                              key={detail.label}
                              className="flex justify-between"
                              style={{ padding: '8px 0', borderBottom: '1px solid #F0F0EB', fontSize: '14px' }}
                            >
                              <span style={{ color: '#666666' }}>{detail.label}</span>
                              <span style={{ fontWeight: 500, color: '#1A1A1A' }}>{detail.value}</span>
                            </li>
                          ))}
                        </ul>

                        {q.description && (
                          <div style={{ background: '#FAFAF8', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
                            <div style={{ fontSize: '12px', fontWeight: 500, color: '#666666', marginBottom: '4px' }}>Description</div>
                            <div style={{ fontSize: '13px', color: '#1A1A1A', lineHeight: 1.5 }}>{q.description}</div>
                          </div>
                        )}

                        {/* Actions */}
                        {q.status === 'pending' && (
                          <button
                            onClick={() => handleSelect(q.id)}
                            className="w-full text-sm font-medium rounded-lg text-white cursor-pointer border-0"
                            style={{ padding: '10px', background: '#043959', fontFamily: "'Lexend', sans-serif", marginTop: '8px' }}
                          >
                            Select This Vendor
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {compareLoaded && compareData.length === 0 && (
              <div
                className="bg-white text-center text-sm"
                style={{ padding: '60px 20px', borderRadius: '16px', border: '1px solid #E5E5E0', color: '#999999' }}
              >
                No quotations found for this service category.
              </div>
            )}
          </>
        )}

        {/* ============ PENDING / SELECTED / ARCHIVED TABS ============ */}
        {activeTab !== 'compare' && activeTab !== 'upload' && (
          <>
            {/* Filter Bar */}
            <div className="flex flex-wrap items-end" style={{ gap: '16px', marginBottom: '24px' }}>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium" style={{ color: '#666666' }}>Service Type:</label>
                <select
                  value={filterService}
                  onChange={(e) => setFilterService(e.target.value)}
                  className="cursor-pointer"
                  style={{ padding: '8px 12px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '14px', fontFamily: "'Lexend', sans-serif", background: 'white', outline: 'none' }}
                >
                  <option value="">All Services</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Quotation Cards Grid (for pending tab) */}
            {activeTab === 'pending' && !loading && quotations.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginBottom: '24px' }}>
                {quotations.map((q) => (
                  <div
                    key={`card-${q.id}`}
                    className="bg-white"
                    style={{
                      border: '2px solid #E5E5E0',
                      borderRadius: '16px',
                      padding: '24px',
                      transition: 'all 0.3s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.08)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: '#1A1A1A' }}>
                        {q.vendor?.name || 'Unknown Vendor'}
                      </div>
                      <div style={{ fontSize: '28px', fontWeight: 700, color: '#043959', marginBottom: '4px' }}>
                        {formatCurrency(q.quoted_amount)}
                      </div>
                    </div>

                    <ul style={{ listStyle: 'none', padding: 0, margin: '16px 0' }}>
                      {[
                        { label: 'Service', value: q.service_category?.name || '-' },
                        { label: 'Validity', value: formatDate(q.validity_date) },
                      ].map((detail) => (
                        <li
                          key={detail.label}
                          className="flex justify-between"
                          style={{ padding: '8px 0', borderBottom: '1px solid #F0F0EB', fontSize: '14px' }}
                        >
                          <span style={{ color: '#666666' }}>{detail.label}</span>
                          <span style={{ fontWeight: 500, color: '#1A1A1A' }}>{detail.value}</span>
                        </li>
                      ))}
                    </ul>

                    <button
                      onClick={() => handleSelect(q.id)}
                      className="w-full text-sm font-medium rounded-lg text-white cursor-pointer border-0"
                      style={{ padding: '10px', background: '#043959', fontFamily: "'Lexend', sans-serif" }}
                    >
                      Select This Vendor
                    </button>
                    <button
                      onClick={() => handleRejectOpen(q.id)}
                      className="w-full text-sm font-medium rounded-lg cursor-pointer"
                      style={{ padding: '10px', background: '#FAFAF8', color: '#1A1A1A', border: '1px solid #E5E5E0', fontFamily: "'Lexend', sans-serif", marginTop: '8px' }}
                    >
                      Reject
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* All Quotations Table */}
            <div
              className="bg-white overflow-hidden"
              style={{ borderRadius: '16px', border: '1px solid #E5E5E0', marginBottom: '24px' }}
            >
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #E5E5E0' }}>
                <h4 className="text-base font-semibold" style={{ color: '#1A1A1A' }}>All Quotations</h4>
              </div>

              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#FAFAF8', borderBottom: '2px solid #E5E5E0' }}>
                      {['Service Category', 'Vendor', 'Amount', 'Received Date', 'Validity', 'Status', 'Actions'].map((h) => (
                        <th key={h} className="text-left uppercase" style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', color: '#666666' }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={7} className="text-center text-sm" style={{ padding: '40px 16px', color: '#999999' }}>
                          Loading quotations...
                        </td>
                      </tr>
                    ) : quotations.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center text-sm" style={{ padding: '40px 16px', color: '#999999' }}>
                          No quotations found
                        </td>
                      </tr>
                    ) : (
                      quotations.map((quotation) => {
                        const serviceName = quotation.service_category?.name || '-';
                        const emoji = getServiceEmoji(serviceName);
                        return (
                          <tr
                            key={quotation.id}
                            className="transition-colors"
                            style={{ borderBottom: '1px solid #F0F0EB' }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#FAFAF8'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            <td style={{ padding: '16px', fontSize: '14px' }}>
                              {emoji} {serviceName}
                            </td>
                            <td style={{ padding: '16px', fontSize: '14px' }}>
                              {quotation.vendor?.name || '-'}
                            </td>
                            <td style={{ padding: '16px', fontSize: '14px', fontWeight: 600 }}>
                              {formatCurrency(quotation.quoted_amount)}
                            </td>
                            <td style={{ padding: '16px', fontSize: '14px' }}>
                              {formatDate(quotation.created_at)}
                            </td>
                            <td style={{ padding: '16px', fontSize: '14px' }}>
                              {formatDate(quotation.validity_date)}
                            </td>
                            <td style={{ padding: '16px' }}>
                              <StatusBadge status={quotation.status} />
                            </td>
                            <td style={{ padding: '16px' }}>
                              <div className="flex gap-2">
                                {quotation.status === 'pending' && (
                                  <>
                                    <button
                                      onClick={() => handleSelect(quotation.id)}
                                      className="inline-flex items-center text-xs font-medium rounded-lg transition-all cursor-pointer text-white border-0"
                                      style={{ padding: '6px 12px', background: '#10B981', fontFamily: "'Lexend', sans-serif" }}
                                    >
                                      Select
                                    </button>
                                    <button
                                      onClick={() => handleRejectOpen(quotation.id)}
                                      className="inline-flex items-center text-xs font-medium rounded-lg transition-all cursor-pointer text-white border-0"
                                      style={{ padding: '6px 12px', background: '#EF4444', fontFamily: "'Lexend', sans-serif" }}
                                    >
                                      Reject
                                    </button>
                                  </>
                                )}
                                <button
                                  onClick={() => handleDelete(quotation.id)}
                                  className="inline-flex items-center text-xs font-medium rounded-lg transition-all cursor-pointer"
                                  style={{ padding: '6px 12px', background: '#FAFAF8', color: '#1A1A1A', border: '1px solid #E5E5E0', fontFamily: "'Lexend', sans-serif" }}
                                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#EF4444'; e.currentTarget.style.color = '#EF4444'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E5E5E0'; e.currentTarget.style.color = '#1A1A1A'; }}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden">
                {loading ? (
                  <div className="text-center text-sm" style={{ padding: '40px 16px', color: '#999999' }}>
                    Loading quotations...
                  </div>
                ) : quotations.length === 0 ? (
                  <div className="text-center text-sm" style={{ padding: '40px 16px', color: '#999999' }}>
                    No quotations found
                  </div>
                ) : (
                  quotations.map((quotation) => {
                    const serviceName = quotation.service_category?.name || '-';
                    const emoji = getServiceEmoji(serviceName);
                    return (
                      <div
                        key={quotation.id}
                        style={{ padding: '16px', borderBottom: '1px solid #F0F0EB' }}
                      >
                        <div className="flex justify-between items-start" style={{ marginBottom: '8px' }}>
                          <div>
                            <div className="text-sm font-semibold" style={{ color: '#1A1A1A' }}>
                              {emoji} {serviceName}
                            </div>
                            <div className="text-xs" style={{ color: '#666666', marginTop: '2px' }}>
                              {quotation.vendor?.name || '-'}
                            </div>
                          </div>
                          <StatusBadge status={quotation.status} />
                        </div>
                        <div className="flex justify-between items-center" style={{ marginBottom: '12px' }}>
                          <div className="text-base font-bold" style={{ color: '#1A1A1A' }}>
                            {formatCurrency(quotation.quoted_amount)}
                          </div>
                          <div className="text-xs" style={{ color: '#666666' }}>
                            Valid till {formatDate(quotation.validity_date)}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {quotation.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleSelect(quotation.id)}
                                className="text-xs font-medium rounded-lg cursor-pointer text-white border-0"
                                style={{ padding: '6px 12px', background: '#10B981', fontFamily: "'Lexend', sans-serif" }}
                              >
                                Select
                              </button>
                              <button
                                onClick={() => handleRejectOpen(quotation.id)}
                                className="text-xs font-medium rounded-lg cursor-pointer text-white border-0"
                                style={{ padding: '6px 12px', background: '#EF4444', fontFamily: "'Lexend', sans-serif" }}
                              >
                                Reject
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleDelete(quotation.id)}
                            className="text-xs font-medium rounded-lg cursor-pointer"
                            style={{ padding: '6px 12px', background: '#FAFAF8', color: '#1A1A1A', border: '1px solid #E5E5E0', fontFamily: "'Lexend', sans-serif" }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Pagination */}
            {meta.last_page > 1 && (
              <div className="flex justify-center gap-2 mt-6">
                {Array.from({ length: meta.last_page }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => fetchQuotations(page)}
                    className="text-sm font-medium rounded-lg transition-all cursor-pointer"
                    style={{
                      padding: '8px 14px',
                      background: page === meta.current_page ? '#043959' : 'white',
                      color: page === meta.current_page ? 'white' : '#1A1A1A',
                      border: `1px solid ${page === meta.current_page ? '#043959' : '#E5E5E0'}`,
                      fontFamily: "'Lexend', sans-serif",
                    }}
                  >
                    {page}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Upload Quotation Modal */}
      <Modal isOpen={showModal} onClose={() => { setShowModal(false); setDocumentFile(null); if (activeTab === 'upload') setActiveTab('pending'); }} title="Upload Quotation" size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormSelect label="Service Category *" name="service_category_id" value={form.service_category_id} onChange={handleChange} options={categoryOptions} required />
          {form.service_category_id === '__other__' && (
            <FormInput label="Category Name *" name="custom_category_name" value={form.custom_category_name} onChange={handleChange} placeholder="Enter service category name" required />
          )}
          <FormInput label="Vendor Name *" name="vendor_name" value={form.vendor_name} onChange={handleChange} placeholder="Enter vendor/company name" required />

          <div>
            <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '6px' }}>Quotation Amount *</label>
            <input
              type="number"
              name="quoted_amount"
              value={form.quoted_amount}
              onChange={handleChange}
              placeholder="Enter amount"
              required
              className="w-full"
              style={{ padding: '10px 12px', border: '1px solid #E5E5E0', borderRadius: '8px', fontSize: '14px', fontFamily: "'Lexend', sans-serif", outline: 'none' }}
            />
          </div>

          <FormInput label="Validity Date *" name="validity_date" type="date" value={form.validity_date} onChange={handleChange} required />

          {/* PDF Upload */}
          <div>
            <label className="block text-sm font-medium" style={{ color: '#1A1A1A', marginBottom: '6px' }}>Upload Quotation PDF *</label>
            <input
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
              disabled={parsing}
              className="block w-full text-sm cursor-pointer"
              style={{ padding: '8px 12px', border: '1px solid #E5E5E0', borderRadius: '8px', fontFamily: "'Lexend', sans-serif", background: 'white' }}
            />
            <p style={{ fontSize: '12px', color: '#999999', marginTop: '4px' }}>
              {parsing ? 'Extracting details from PDF...' : documentFile ? 'PDF attached. Fields auto-filled where possible.' : 'Maximum file size: 10MB'}
            </p>
          </div>

          <FormTextarea label="Scope of Work" name="description" value={form.description} onChange={handleChange} placeholder="Brief description of what's included in this quotation" />

          <div className="flex justify-end gap-3 pt-4" style={{ borderTop: '1px solid #E5E5E0' }}>
            <button
              type="button"
              onClick={() => { setShowModal(false); if (activeTab === 'upload') setActiveTab('pending'); }}
              className="text-sm font-medium rounded-lg transition-all cursor-pointer"
              style={{ padding: '10px 20px', background: '#FAFAF8', color: '#1A1A1A', border: '1px solid #E5E5E0', fontFamily: "'Lexend', sans-serif" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="text-sm font-medium rounded-lg text-white transition-all cursor-pointer border-0"
              style={{ padding: '10px 20px', background: '#043959', fontFamily: "'Lexend', sans-serif", opacity: submitting ? 0.7 : 1 }}
            >
              {submitting ? 'Uploading...' : 'Upload Quotation'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Reject Quotation Modal */}
      <Modal isOpen={showRejectModal} onClose={() => setShowRejectModal(false)} title="Reject Quotation">
        <div className="space-y-4">
          <p className="text-sm" style={{ color: '#666666' }}>
            Please provide a reason for rejecting this quotation.
          </p>
          <FormTextarea
            label="Rejection Reason"
            name="rejection_reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Enter reason for rejection..."
          />
          <div className="flex justify-end gap-3 pt-4" style={{ borderTop: '1px solid #E5E5E0' }}>
            <button
              type="button"
              onClick={() => setShowRejectModal(false)}
              className="text-sm font-medium rounded-lg transition-all cursor-pointer"
              style={{ padding: '10px 20px', background: '#FAFAF8', color: '#1A1A1A', border: '1px solid #E5E5E0', fontFamily: "'Lexend', sans-serif" }}
            >
              Cancel
            </button>
            <button
              onClick={handleRejectSubmit}
              className="text-sm font-medium rounded-lg text-white transition-all cursor-pointer border-0"
              style={{ padding: '10px 20px', background: '#EF4444', fontFamily: "'Lexend', sans-serif" }}
            >
              Reject Quotation
            </button>
          </div>
        </div>
      </Modal>
    </AppLayout>
  );
}
